import React, { useMemo } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { ArrowCounterClockwise, Warning } from '@phosphor-icons/react';

const FIELDS = [
  { key: 'first_name', label: 'Vorname / Firma', placeholder: 'Max' },
  { key: 'last_name', label: 'Nachname / Zusatz', placeholder: 'Mustermann' },
  { key: 'email', label: 'E-Mail', placeholder: 'max@example.de', type: 'email' },
  { key: 'phone', label: 'Telefon', placeholder: '030 1234567' },
  { key: 'address', label: 'Straße + Nr.', placeholder: 'Musterstr. 42', full: true },
  { key: 'postal_code', label: 'PLZ', placeholder: '10115' },
  { key: 'city', label: 'Ort', placeholder: 'Berlin' },
  { key: 'country', label: 'Land', placeholder: 'Deutschland' },
];

/**
 * CustomerOverrideFields
 *
 * Shows editable fields for customer data scoped to THIS document only.
 * - `customer` = the original customer record (stammdaten)
 * - `override` = the current per-document override (may be null)
 * - `onChange(newOverride|null)` is called whenever the user edits a field.
 *   When the user resets a field to the original value (or clears all),
 *   we emit a pruned object — empty = null to clear.
 *
 * We always display the effective value (override ?? stammdaten), but
 * only fields that differ from stammdaten go into the emitted payload.
 */
const CustomerOverrideFields = ({ customer, override, onChange, disabled = false, title = 'Kundendaten (nur für dieses Dokument)' }) => {
  const base = customer || {};
  const effective = useMemo(() => {
    const out = {};
    for (const f of FIELDS) {
      out[f.key] = (override && override[f.key] != null && override[f.key] !== '')
        ? override[f.key]
        : (base[f.key] ?? '');
    }
    return out;
  }, [customer, override]);

  const isChangedFromBase = (key, value) => {
    const basev = (base[key] ?? '').toString().trim();
    const v = (value ?? '').toString().trim();
    return v !== basev && v !== '';
  };

  const emitChange = (nextEffective) => {
    const payload = {};
    for (const f of FIELDS) {
      const v = (nextEffective[f.key] ?? '').toString();
      if (isChangedFromBase(f.key, v)) {
        payload[f.key] = v.trim();
      }
    }
    onChange(Object.keys(payload).length ? payload : null);
  };

  const handleField = (key, value) => {
    emitChange({ ...effective, [key]: value });
  };

  const resetAll = () => onChange(null);

  const hasAny = FIELDS.some((f) => isChangedFromBase(f.key, effective[f.key]));

  return (
    <div className="p-3 bg-[#F7F8FA] border border-[#E2E4E9] rounded-sm space-y-3" data-testid="customer-override-fields">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Label className="overline mb-0">{title}</Label>
          {hasAny && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#B25A00] bg-[#FFF3E0] border border-[#F5C27E] rounded-sm px-1.5 py-0.5">
              <Warning weight="fill" size={10} /> Abweichend von Stammdaten
            </span>
          )}
        </div>
        {hasAny && !disabled && (
          <Button type="button" variant="ghost" size="sm" onClick={resetAll} data-testid="customer-override-reset">
            <ArrowCounterClockwise size={14} className="mr-1" /> Auf Stammdaten zurücksetzen
          </Button>
        )}
      </div>

      <p className="text-xs text-[#8A94A6]">
        Änderungen gelten <strong>nur für dieses Dokument</strong> (Mietvertrag / Rechnung / PDF). Der Kundenstamm bleibt unverändert.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {FIELDS.map((f) => {
          const changed = isChangedFromBase(f.key, effective[f.key]);
          return (
            <div key={f.key} className={f.full ? 'sm:col-span-2' : ''}>
              <Label className="overline">{f.label}</Label>
              <Input
                type={f.type || 'text'}
                value={effective[f.key] || ''}
                onChange={(e) => handleField(f.key, e.target.value)}
                placeholder={f.placeholder}
                disabled={disabled}
                className={changed ? 'border-[#F5C27E] bg-[#FFFBF2]' : ''}
                data-testid={`cust-override-${f.key}`}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CustomerOverrideFields;
